// App.tsx

import React, { useState, useCallback, useMemo } from 'react';
import { Container, Button, Row, Col, Form, Badge, Dropdown, DropdownButton, ButtonGroup } from 'react-bootstrap';
import { ArrowRepeat, EyeFill, EyeSlashFill } from 'react-bootstrap-icons';
import FlashCard from './FlashCard-component-nov-2025-v01-gemini';
import { allDataDocuments, FlashCardItem, initialDocument } from './dataDocuments';

// Function to shuffle an array
const shuffleArray = (array: FlashCardItem[]): FlashCardItem[] => {
  let newArray = [...array];
  for (let i = newArray.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
  }
  return newArray;
};

const FLASH_CARD_APP: React.FC = () => {
  // State 1: Holds all documents and their current confidence states
  const [documents, setDocuments] = useState(allDataDocuments);
  // State 2: Holds the ID of the currently selected document
  const [activeDocId, setActiveDocId] = useState(initialDocument.docId);
  // State 3: Holds the currently selected category filter ("ALL" by default)
  const [activeCategory, setActiveCategory] = useState<string>("ALL");
  // State 4: Toggle to switch between <100% and 100% items
  const [showMastered, setShowMastered] = useState(false);
  // State 5: Key used to force FlashCard state reset on shuffle (NEW STATE)
  const [shuffleKey, setShuffleKey] = useState(0); 

  // Memoized hook to get the active document and its cards
  const activeDocument = useMemo(() => {
    return documents.find(doc => doc.docId === activeDocId) || initialDocument;
  }, [documents, activeDocId]);

  const cards = activeDocument.cards;

  // Handler to update the confidence of a specific card by its ID
  const updateConfidence = useCallback((cardId: number, newConfidence: number) => {
    setDocuments(prevDocuments =>
      prevDocuments.map(doc => {
        if (doc.docId !== activeDocId) {
          return doc;
        }
        return {
          ...doc,
          cards: doc.cards.map(card =>
            card.id === cardId ? { ...card, confidence: newConfidence } : card
          ),
        };
      })
    );
  }, [activeDocId]);

  // Handler for document change: resets category filter to "ALL"
  const handleDocChange = (docId: number) => {
    setActiveDocId(docId);
    setActiveCategory("ALL"); // Reset category filter on topic change
    setShuffleKey(prev => prev + 1); // Reset card flip state
  };

  // Handler for the shuffle button (UPDATED LOGIC)
  const handleShuffle = () => {
    setDocuments(prevDocuments =>
      prevDocuments.map(doc => {
        if (doc.docId === activeDocId) {
          return {
            ...doc,
            cards: shuffleArray(doc.cards),
          };
        }
        return doc;
      })
    );
    // CRITICAL: Increment the key to force FlashCard to reset its internal flip state
    setShuffleKey(prev => prev + 1);
  };
  
  // 1. Get unique categories for the current document
  const categories = useMemo(() => {
    const categorySet = new Set<string>(cards.map(card => card.category));
    return ["ALL", ...Array.from(categorySet).sort()];
  }, [cards]);

  // 2. Filter list by Category and then by Confidence (Mastered/Learning)
  const displayedCards = cards
    .filter(card => activeCategory === "ALL" || card.category === activeCategory)
    .filter(card =>
      showMastered
        ? card.confidence === 100 
        : card.confidence < 100
    );

  // Metrics
  const totalItems = cards.length;
  const masteredItems = cards.filter(card => card.confidence === 100).length;

  return (
    <Container className="my-5">
      <header className="text-center mb-4">
        <h1>✈️ Flash Card Trainer</h1>
        <p className="lead">{activeDocument.description}</p>
      </header>
      
      {/* Control Panel */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 p-3 bg-light rounded shadow-sm">

        {/* 1. Document Selector Dropdown */}
        <DropdownButton
            as={ButtonGroup}
            key="document-selector"
            id="document-selector"
            variant="dark"
            title={activeDocument.title}
            className="mb-2 mb-md-0 me-3"
        >
          {allDataDocuments.map(doc => (
            <Dropdown.Item 
              key={doc.docId} 
              active={doc.docId === activeDocId}
              onClick={() => handleDocChange(doc.docId)}
            >
              {doc.title}
            </Dropdown.Item>
          ))}
        </DropdownButton>
        
        {/* 2. Category Filter Dropdown */}
        <DropdownButton
            as={ButtonGroup}
            key="category-filter"
            id="category-filter"
            variant="secondary"
            title={`Category: ${activeCategory}`}
            className="mb-2 mb-md-0 me-3"
        >
          {categories.map(category => (
            <Dropdown.Item 
              key={category} 
              active={category === activeCategory}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </Dropdown.Item>
          ))}
        </DropdownButton>

        {/* Status Badges */}
        <div>
          <Badge bg="primary" className="me-2">Total: {activeCategory === "ALL" ? totalItems : displayedCards.length} {activeCategory === "ALL" ? "" : `(${activeCategory})`}</Badge>
          <Badge bg="success">Mastered: {masteredItems}</Badge>
        </div>

        {/* Shuffle Button */}
        <Button onClick={handleShuffle} variant="outline-primary" className="me-3">
          <ArrowRepeat className="me-1" /> Randomize
        </Button>
        
        {/* Toggle Switch */}
        <Form.Check 
          type="switch"
          id="custom-switch"
          label={
            showMastered 
              ? <span><EyeFill className="me-1" /> **Mastered (100%)**</span> 
              : <span><EyeSlashFill className="me-1" /> **Learning (&lt;100%)**</span>
          }
          checked={showMastered}
          onChange={(e) => setShowMastered(e.target.checked)}
          reverse
        />
      </div>

      <Row>
        {/* Map over the filtered cards to display the FlashCard component */}
        {displayedCards.map(card => (
          <Col md={6} lg={4} key={card.id} className="d-flex align-items-stretch">
            {/* Pass the shuffleKey to force remount and state reset */}
            <FlashCard 
              key={`${card.id}-${shuffleKey}`} // Combined key to force reset on shuffle
              card={card} 
              onUpdateConfidence={updateConfidence} 
            />
          </Col>
        ))}
      </Row>
      
      {/* Message when no cards are showing */}
      {displayedCards.length === 0 && (
        <div className="text-center p-5 border rounded bg-white">
          <p className="h4 text-muted">
            {showMastered 
              ? `No items have reached 100% confidence in the selected filters!` 
              : `All items have been mastered under the selected filters!`
            }
          </p>
        </div>
      )}
    </Container>
  );
};

export default FLASH_CARD_APP;